import React, { useState } from "react";
import { formatAuthError, signInWithGoogle, signOutUser } from "../billing/firebaseAuth";
import { isFirebaseConfigured } from "../billing/billingConfig";
import { useOptionalAuth } from "../auth/AuthProvider";
import Link from "./Link";

export default function BillingAuthPanel({ user, compact = false }) {
  const auth = useOptionalAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!isFirebaseConfigured()) {
    return (
      <div
        role="status"
        className="rounded-2xl border border-ink/8 bg-bg1 p-4 text-sm leading-relaxed text-muted"
      >
        Website sign-in is not configured in this environment. Public Checkout stays closed until Firebase and the
        Billing API are connected.
      </div>
    );
  }

  if (user) {
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
        <span>
          Signed in as <strong className="text-ink">{user.email || "account"}</strong>
        </span>
        <button
          type="button"
          onClick={() => (auth?.signOut ? auth.signOut() : signOutUser())}
          className="inline-flex min-h-11 items-center rounded-full px-3 py-2 font-semibold text-ink ring-1 ring-ink/10 hover:bg-bg1"
        >
          Sign out
        </button>
        <Link to="/account" className="text-sm font-semibold text-ink underline underline-offset-4">
          My account
        </Link>
      </div>
    );
  }

  async function runGoogle() {
    setBusy(true);
    setError(null);
    try {
      if (auth?.signIn) await auth.signIn();
      else await signInWithGoogle();
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={compact ? "" : "max-w-md rounded-[28px] border border-hint/20 bg-white p-5 shadow-card"}>
      <h2 className="text-base font-semibold text-ink">Sign in with Google</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted">
        Use the same Google account as the Stratify iOS app so billing and entitlements match.
      </p>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={runGoogle}
        className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-ink px-4 text-sm font-semibold text-white disabled:opacity-60"
      >
        {busy ? "Working…" : "Sign in with Google"}
      </button>
    </div>
  );
}
